import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Check, LockKeyhole } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type ComplaintDetail, type ComplaintPerson } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { FormPage } from '../components/FormPage';
import { Field, Input, LoadingBlock, Textarea, useToast } from '../components/ui';
import { AttachmentsField, useUploads } from '../components/Attachments';

/** Past this many people the picker gets a box to find someone by name. */
const FIND_FROM = 7;

/**
 * Route: `/complaints/new` — a complaint about someone on the client's projects:
 * whom, a title, the details, and any files or voice notes. It goes to
 * Viganium's management, never to the person it is about.
 */
export function ComplaintFormPage() {
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const uploads = useUploads();

  const [form, setForm] = useState({ subjectUserId: '', title: '', details: '' });

  const people = useQuery({
    queryKey: ['complaint-people'],
    queryFn: () => api.get<ComplaintPerson[]>('/complaints/people'),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<ComplaintDetail>('/complaints', {
        subjectUserId: form.subjectUserId,
        title: form.title.trim(),
        details: form.details.trim() || undefined,
        fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
      }),
    onSuccess: (complaint) => {
      toast.success(t('complaints.created', { number: complaint.number }));
      void queryClient.invalidateQueries({ queryKey: ['complaints'] });
      navigate(`/complaints/${complaint.id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // The details, a file or a voice note — something has to say what happened.
  const saysSomething = form.details.trim() !== '' || uploads.fileIds.length > 0;

  return (
    <FormPage
      title={t('complaints.new')}
      backTo="/complaints"
      backLabel={t('complaints.title')}
      submitLabel={t('common.send')}
      submitting={create.isPending}
      disabled={!form.subjectUserId || !form.title.trim() || !saysSomething || uploads.busy}
      loading={people.isLoading}
      error={people.error}
      onRetry={() => void people.refetch()}
      onSubmit={() => create.mutate()}
    >
      <div className="space-y-5">
        <p className="flex items-start gap-2 rounded-md border border-primary/25 bg-primary/6 px-3 py-2.5 text-body-sm text-[var(--text-secondary)]">
          <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
          {t('complaints.private')}
        </p>

        {people.data ? (
          <PersonPicker
            people={people.data}
            value={form.subjectUserId}
            onChange={(subjectUserId) => setForm({ ...form, subjectUserId })}
          />
        ) : (
          <LoadingBlock />
        )}

        <Field label={t('complaints.titleField')} required hint={t('complaints.titleHint')}>
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            maxLength={300}
            dir="auto"
          />
        </Field>

        <Field label={t('complaints.details')} hint={t('complaints.detailsHint')}>
          <Textarea
            value={form.details}
            onChange={(e) => setForm({ ...form, details: e.target.value })}
            onPaste={uploads.onPaste}
            className="min-h-[160px]"
            dir="auto"
          />
        </Field>

        <AttachmentsField uploads={uploads} />

        {form.title.trim() !== '' && !saysSomething && (
          <p className="text-caption text-muted">{t('complaints.needsSomething')}</p>
        )}
      </div>
    </FormPage>
  );
}

/**
 * Whom the complaint is about: the people on the client's projects, each with
 * a job title and the projects they are on. A radio group — one choice, the
 * arrow keys move it — drawn as cards.
 */
function PersonPicker({
  people,
  value,
  onChange,
}: {
  people: ComplaintPerson[];
  value: string;
  onChange: (id: string) => void;
}) {
  const { t, localized } = useI18n();
  const [find, setFind] = useState('');

  const shown = useMemo(() => {
    const needle = find.trim().toLowerCase();
    if (!needle) return people;
    // The chosen one stays in view while someone else is looked for.
    return people.filter(
      (p) =>
        p.id === value ||
        [p.fullName, p.fullNameAr ?? '', p.jobTitle ?? ''].some((text) => text.toLowerCase().includes(needle)),
    );
  }, [people, find, value]);

  return (
    <fieldset>
      <legend className="mb-1.5 flex items-center gap-1 text-body-sm font-medium text-[var(--text-secondary)]">
        {t('complaints.about')}
        <span className="text-primary dark:text-primary-400">*</span>
      </legend>

      {people.length === 0 ? (
        <p className="rounded-md border hairline bg-[var(--surface-sunken)] px-3 py-2 text-body-sm text-muted">
          {t('complaints.noPeople')}
        </p>
      ) : (
        <>
          {people.length >= FIND_FROM && (
            <Input
              type="search"
              value={find}
              onChange={(e) => setFind(e.target.value)}
              placeholder={t('complaints.find')}
              aria-label={t('complaints.find')}
              className="mb-2 max-w-sm"
            />
          )}
          <div role="radiogroup" aria-label={t('complaints.about')} className="grid gap-2 sm:grid-cols-2">
            {shown.map((person) => {
              const chosen = person.id === value;
              return (
                <label
                  key={person.id}
                  className={clsx(
                    'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors duration-fast',
                    'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/60',
                    chosen
                      ? 'border-primary bg-primary/6 ring-1 ring-inset ring-primary/40'
                      : 'hairline hover:bg-[var(--surface-sunken)]',
                  )}
                >
                  <input
                    type="radio"
                    name="complaint-subject"
                    value={person.id}
                    checked={chosen}
                    onChange={() => onChange(person.id)}
                    className="sr-only"
                  />
                  <span
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/15 text-caption font-semibold text-primary-600 dark:text-primary-300"
                    aria-hidden
                  >
                    {initials(localized(person))}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-body-sm font-medium text-[var(--text-primary)]">
                      {localized(person)}
                    </span>
                    {person.jobTitle && <span className="block truncate text-caption text-muted">{person.jobTitle}</span>}
                    {person.projects.length > 0 && (
                      <span className="mt-0.5 block truncate text-caption text-faint">
                        {person.projects.map((p) => localized(p)).join(' · ')}
                      </span>
                    )}
                  </span>
                  <Check
                    className={clsx('mt-0.5 h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400', !chosen && 'invisible')}
                    aria-hidden
                  />
                </label>
              );
            })}
          </div>
          {shown.length === 0 && <p className="mt-2 text-caption text-muted">{t('complaints.noMatch')}</p>}
          <span className="mt-1 block text-caption text-faint">{t('complaints.aboutHint')}</span>
        </>
      )}
    </fieldset>
  );
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
